import { useState, useEffect, useCallback } from "react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2, Edit, UserCircle, CalendarOff, Gift, ShieldAlert,
  ArrowLeft, GraduationCap, Wrench, MapPin, Star, Clock, FileText, Percent, Timer,
} from "lucide-react";
import EmployeeTravelBonusTab from "../EmployeeTravelBonusTab";
import PayslipDialog from "./PayslipDialog";
import TimesheetDialog from "./TimesheetDialog";
import { EMPLOYEE_TYPES } from "@/lib/employeeConstants";
import InformationsTab from "./tabs/InformationsTab";
import AbsencesTab from "./tabs/AbsencesTab";
import BonusesTab from "./tabs/BonusesTab";
import PVsTab from "./tabs/PVsTab";
import TrainingsTab from "./tabs/TrainingsTab";
import SAVTab from "./tabs/SAVTab";
import OrdersTab from "./tabs/OrdersTab";
import LifeEventsTab from "./tabs/LifeEventsTab";
import ChronologieTab from "./tabs/ChronologieTab";
import CommissionsTab from "./tabs/CommissionsTab";
import HoursTab from "./tabs/HoursTab";

const DEFAULT_TIMELINE_FILTER = {
  from: "", to: "",
  types: { absence: true, bonus: true, pv: true, training: true, "life-event": true, sav: true, chantier: true },
};

export const EmployeeDetailView = ({ employee, onBack, onEdit }) => {
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [absences, setAbsences] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [pvs, setPvs] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [savData, setSavData] = useState({ savs: [], count: 0, total_cost: 0 });
  const [lifeEvents, setLifeEvents] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [ordersWorked, setOrdersWorked] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState(DEFAULT_TIMELINE_FILTER);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, absRes, bonRes, pvRes, trainRes, savRes, lifeRes, salRes, ordRes] = await Promise.all([
        api.getEmployeeStats(employee.id, { year }),
        api.getAbsences(employee.id),
        api.getEmployeeBonuses(employee.id),
        api.getEmployeePVs(employee.id),
        api.getTrainings(employee.id),
        api.getSAVsForEmployee(employee.id),
        api.getEmployeeLifeEvents(employee.id),
        api.getEmployeeSalaries(employee.id),
        api.getEmployeeOrders(employee.id),
      ]);
      setStats(statsRes.data);
      setAbsences(absRes.data);
      setBonuses(bonRes.data);
      setPvs(pvRes.data);
      setTrainings(trainRes.data);
      setSavData(savRes.data);
      setLifeEvents(lifeRes.data || []);
      setSalaries(salRes.data || []);
      setOrdersWorked(ordRes.data?.items || []);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [employee.id, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading || !stats) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= new Date().getFullYear() - 5; y--) years.push(y);

  const hasExpiringTraining = trainings.some(
    t => t.expiry_date && ((new Date(t.expiry_date) - new Date()) / 86400000) <= 270
  );

  return (
    <div className="space-y-6" data-testid="employee-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="back-to-employees">
            <ArrowLeft className="h-4 w-4 mr-2" /> Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{employee.last_name} {employee.first_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={EMPLOYEE_TYPES[employee.employee_type]?.color}>
                {EMPLOYEE_TYPES[employee.employee_type]?.label}
              </Badge>
              {employee.is_interim && <Badge className="bg-amber-100 text-amber-800 border-amber-300">Intérim</Badge>}
              {employee.exit_date
                ? <Badge variant="outline">Parti le {formatDate(employee.exit_date)}</Badge>
                : <Badge className="bg-green-100 text-green-800">Actif</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={onEdit}><Edit className="h-4 w-4 mr-2" /> Modifier</Button>
          <PayslipDialog employee={employee} />
          <TimesheetDialog employee={employee} />
        </div>
      </div>

      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info"><UserCircle className="h-4 w-4 mr-1" /> Informations</TabsTrigger>
          <TabsTrigger value="absences" data-testid="tab-absences"><CalendarOff className="h-4 w-4 mr-1" /> Absences</TabsTrigger>
          <TabsTrigger value="bonuses" data-testid="tab-bonuses"><Gift className="h-4 w-4 mr-1" /> Primes</TabsTrigger>
          <TabsTrigger value="pvs" data-testid="tab-pvs"><ShieldAlert className="h-4 w-4 mr-1" /> PV</TabsTrigger>
          <TabsTrigger value="trainings" data-testid="tab-trainings">
            <GraduationCap className="h-4 w-4 mr-1" /> Formations
            {hasExpiringTraining && <Badge variant="destructive" className="h-5 min-w-5 text-xs px-1 ml-1">!</Badge>}
          </TabsTrigger>
          <TabsTrigger value="sav" data-testid="tab-sav">
            <Wrench className="h-4 w-4 mr-1" /> SAV
            {savData.count > 0 && <Badge variant="outline" className="h-5 min-w-5 text-xs px-1 ml-1">{savData.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">
            <FileText className="h-4 w-4 mr-1" /> Commandes
          </TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours">
            <Timer className="h-4 w-4 mr-1" /> Heures
          </TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions">
            <Percent className="h-4 w-4 mr-1" /> Commissions
          </TabsTrigger>
          <TabsTrigger value="travel-bonus" data-testid="tab-travel-bonus">
            <MapPin className="h-4 w-4 mr-1" /> Primes déplacement
          </TabsTrigger>
          <TabsTrigger value="life-events" data-testid="tab-life-events">
            <Star className="h-4 w-4 mr-1" /> Événements marquants
            {lifeEvents.length > 0 && <Badge variant="outline" className="h-5 min-w-5 text-xs px-1 ml-1">{lifeEvents.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <Clock className="h-4 w-4 mr-1" /> Chronologie
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <InformationsTab employee={employee} salaries={salaries} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="absences">
          <AbsencesTab employee={employee} stats={stats} absences={absences} year={year} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="bonuses">
          <BonusesTab employee={employee} stats={stats} bonuses={bonuses} year={year} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="pvs">
          <PVsTab employee={employee} stats={stats} pvs={pvs} year={year} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="trainings">
          <TrainingsTab employee={employee} trainings={trainings} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="sav">
          <SAVTab savData={savData} />
        </TabsContent>

        <TabsContent value="orders">
          <OrdersTab employee={employee} />
        </TabsContent>

        <TabsContent value="hours">
          <HoursTab employee={employee} year={year} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="commissions">
          <CommissionsTab employee={employee} />
        </TabsContent>

        <TabsContent value="travel-bonus">
          <EmployeeTravelBonusTab employeeId={employee.id} />
        </TabsContent>

        <TabsContent value="life-events">
          <LifeEventsTab employee={employee} lifeEvents={lifeEvents} onRefresh={fetchAll} />
        </TabsContent>

        <TabsContent value="timeline">
          <ChronologieTab
            employee={employee}
            absences={absences} bonuses={bonuses} pvs={pvs}
            trainings={trainings} lifeEvents={lifeEvents} savs={savData.savs}
            orders={ordersWorked}
            filter={timelineFilter} setFilter={setTimelineFilter}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeDetailView;
